import {parseApiDate} from '../utils/dateTime';

// Локальные подписи разбора гардероба: коды стабильны на backend, локализация — на клиенте
// (тот же приём, что в support/itemLifecycle.ts).
import {DeclutterAction} from '../service/DeclutterApi';

export const ACTION_LABELS: Record<DeclutterAction, string> = {
    KEEP: 'Оставить',
    SELL: 'Продать',
    DONATE: 'Отдать',
    GIVE_AWAY: 'Отдать другу',
    DISPOSE: 'Утилизировать',
    MOVE_TO_HOME: 'Донашивать дома',
    MOVE_TO_WORK: 'Перевести в рабочие',
};

/**
 * Основной ряд действий карточки. «Донашивать» вынесено во второй ряд: это не расставание,
 * и смешивать его с основными вариантами — размывать решение.
 */
export const PRIMARY_ACTIONS: DeclutterAction[] = ['KEEP', 'SELL', 'DONATE', 'DISPOSE'];
export const LADDER_ACTIONS: DeclutterAction[] = ['MOVE_TO_HOME', 'MOVE_TO_WORK'];

// Подписи причин очереди. `DUPLICATE` backend ставит только по найденной векторной паре (R3);
// переполненная функциональная роль — это `ROLE_SURPLUS`, а `HAS_REPLACEMENT` остаётся ради
// замороженных очередей старых сессий.
export const BUCKET_LABELS: Record<string, string> = {
    DUPLICATE: 'Дубль',
    DEAD_STOCK: 'Не носили',
    POOR_CONDITION: 'Состояние',
    ROLE_SURPLUS: 'Роль закрыта',
    HAS_REPLACEMENT: 'Есть замена',
    LOW_COMPATIBILITY: 'Слабая сочетаемость',
    LOW_SCORE: 'Низкая оценка',
    OTHER: 'Прочее',
};

export const MODE_OPTIONS: {value: string; label: string; hint: string}[] = [
    {value: 'SOFT', label: 'Мягкий', hint: 'Сомнения — в пользу вещи'},
    {value: 'NORMAL', label: 'Обычный', hint: 'Сбалансированные пороги'},
    {value: 'HARD', label: 'Жёсткий', hint: 'Решительнее к расставанию'},
];

export function actionLabel(action: string | null | undefined): string | null {
    if (!action) return null;
    return ACTION_LABELS[action as DeclutterAction] ?? action;
}

export function bucketLabel(bucket: string | null | undefined): string | null {
    if (!bucket) return null;
    return BUCKET_LABELS[bucket] ?? bucket;
}

/**
 * Подпись прогресса. Именно «5 из 8 выбрано на выбытие», а не проценты: «15 / 23 — 65 %»
 * при цели «уменьшить с 23 до 15» читается неоднозначно.
 *
 * Разбор идёт по всему гардеробу, поэтому цели может не быть вовсе: если система не нашла ни
 * одного повода, знаменатель не выдумывается — показываем только принятые решения.
 */
export function progressLabel(decidedForRemoval: number, targetCount: number): string {
    if (targetCount <= 0) {
        return `${decidedForRemoval} выбрано на выбытие`;
    }
    return `${decidedForRemoval} из ${targetCount} выбрано на выбытие`;
}

/** Дата последней носки в человеческом виде; без даты — честное «не носили». */
export function lastWornLabel(iso: string | null | undefined): string {
    const date = parseApiDate(iso);
    if (!date) return 'Ни разу не носили';
    // Календарные месяцы, а не «30 дней»: иначе 31 января и 1 марта дают «месяц назад» с ошибкой
    // в обе стороны, и к концу года набегает лишний месяц.
    const now = new Date();
    let months = (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
    if (now.getDate() < date.getDate()) months -= 1;
    if (months < 1) return 'В этом месяце';
    if (months === 1) return 'Месяц назад';
    return `${months} мес. назад`;
}

/** Направления физического шага: что именно надо сделать руками. */
export const BUCKET_ACTIONS: Record<string, string> = {
    TO_SELL: 'Подготовить к продаже',
    TO_DONATE: 'Подготовить к отдаче',
    TO_DISPOSE: 'Вынести на утилизацию или переработку',
};

export const OUTCOME_LABELS: Record<string, string> = {
    REMOVED_SOLD: 'Продано',
    REMOVED_DONATED: 'Пожертвовано',
    REMOVED_GIVEN_AWAY: 'Отдано',
    REMOVED_DISPOSED: 'Утилизировано',
    REMOVED_RECYCLED: 'Сдано на переработку',
    RETURNED_TO_WARDROBE: 'Возвращено в гардероб',
};

export function bucketActionLabel(bucket: string): string {
    return BUCKET_ACTIONS[bucket] ?? bucket;
}

export function outcomeLabel(code: string): string {
    return OUTCOME_LABELS[code] ?? code;
}

/**
 * Заголовок отчёта. До физического выбытия это «Итоги решений»: место не освобождено, пока
 * вещи лежат в пакете у шкафа, и называть это итогами разбора было бы неправдой.
 */
export function reportTitle(stage: string): string {
    return stage === 'FINAL' ? 'Итоги разбора' : 'Итоги решений';
}

/**
 * Чипы возражений. Короткие формулировки от первого лица: человек нажимает то, что у него
 * в голове, а не выбирает из терминов приложения.
 */
export const OBJECTION_CHIPS: {code: string; label: string}[] = [
    {code: 'SUNK_COST', label: 'Жалко'},
    {code: 'PRICE', label: 'Дорогая'},
    {code: 'JUST_IN_CASE', label: 'Вдруг понадобится'},
    {code: 'REPLACEMENT_DOUBT', label: 'Нечем заменить'},
    {code: 'SENTIMENTAL', label: 'Дорога как память'},
];

export function objectionLabel(code: string): string {
    return OBJECTION_CHIPS.find(c => c.code === code)?.label ?? code;
}

/** Подписи статусов сессии для стадий страницы и истории. */
export const SESSION_STATUS_LABELS: Record<string, string> = {
    DRAFT: 'Черновик',
    IN_PROGRESS: 'Идёт разбор',
    DECISIONS_DONE: 'Решения приняты',
    COMPLETED: 'Завершён',
    ABANDONED: 'Брошен',
};

export function sessionStatusLabel(status: string | null | undefined): string {
    if (!status) return '';
    return SESSION_STATUS_LABELS[status] ?? status;
}

/** Варианты «не продалось»: вещь уходит другим каналом или возвращается в шкаф. */
export const UNSOLD_RESOLUTIONS: {value: string; label: string}[] = [
    {value: 'DONATE', label: 'Отдать'},
    {value: 'DISPOSE', label: 'Утилизировать'},
    {value: 'RETURN', label: 'Вернуть в гардероб'},
];

/**
 * Тексты ошибок старта. Backend отдаёт коды, а не фразы: `SESSION_ALREADY_OPEN` — не ошибка
 * пользователя, а приглашение продолжить, поэтому подпись без упрёка.
 */
export const START_ERROR_MESSAGES: Record<string, string> = {
    NO_ITEMS: 'В гардеробе нет вещей — разбирать нечего.',
    NO_CANDIDATES: 'Система не нашла поводов для быстрого разбора — начните полный.',
    SESSION_ALREADY_OPEN: 'Разбор уже идёт — продолжите его или завершите решения.',
};

export function startErrorMessage(code: string | null): string {
    return (code && START_ERROR_MESSAGES[code]) || 'Не удалось начать разбор';
}
